import { useCallback, useEffect, useState } from 'react';
import { PaymentElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import Checkable from '../payment/Checkable';
import { useTranslation } from 'react-i18next';
import { Button, Grid2, Typography } from '@mui/material';
import { minimumPaymentAmount } from '../../../../../config';

function StripeInnerForm({ onStartTransaction, methodInfo, onCancel, amount, maxAmount }) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) {
            return;
        }

        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            console.error('Error confirming payment:', error);
            return;
        }

        if (setupIntent) {
            console.log('Payment successful:', setupIntent);
            // TODO: continue to take this and create a transaction, finish up order and tick status
            onStartTransaction({
                customerId: methodInfo?.customerId,
                setupResult: setupIntent,
            });
        }
    };
    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={{ layout: 'tabs' }} />
            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                <Grid2 size={{ xs: 6 }}>
                    <Button fullWidth onClick={onCancel}>
                        {t('common.cancel')}
                    </Button>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                    <Button
                        fullWidth
                        type="submit" // cannot do a split if less than 1 usd is one of the parts (either the one paid or the remaining portion)
                        disabled={
                            !stripe ||
                            (amount && maxAmount
                                ? amount < minimumPaymentAmount ||
                                  (maxAmount - amount < minimumPaymentAmount &&
                                      amount !== maxAmount)
                                : false)
                        }
                    >
                        {t('common.pay')}
                    </Button>
                </Grid2>
            </Grid2>
        </form>
    );
}

function StripePaymentBlock({ onCancel, paymentMethod, onStartTransaction, amount, maxAmount }) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const [cardToUse, setCardToUse] = useState(() => {
        return paymentMethod?.methodInfo?.oldCards[0]?.id || null;
    });
    useEffect(() => {
        setCardToUse(paymentMethod?.methodInfo?.oldCards[0]?.id || null);
    }, [paymentMethod?.methodInfo?.oldCards]);

    const payWithOldCard = useCallback(() => {
        onStartTransaction({
            customerId: paymentMethod?.methodInfo?.customerId,
            oldPaymentMethod: cardToUse,
        });
    }, [cardToUse, onStartTransaction, paymentMethod?.methodInfo?.customerId]);

    return (
        <Grid2 container direction="column" spacing={2}>
            <Grid2 size={{ xs: 12 }}>
                <Typography variant="h6">
                    {paymentMethod.name} {t('paymentmethods.paymentBlock')}
                </Typography>
            </Grid2>
            {paymentMethod?.methodInfo?.oldCards?.length > 0 && (
                <Grid2 size={{ xs: 12 }}>
                    {paymentMethod.methodInfo.oldCards.map((card) => (
                        <Checkable
                            key={card.id}
                            attributes={{
                                type: 'radio',
                                name: 'oldCard',
                                value: card.id,
                                checked: cardToUse === card.id,
                                onChange: () => setCardToUse(card.id),
                            }}
                            label={t('widgetReservationInfo.card.oldCard', {
                                cardLast4: card.cardLast4,
                                expMonth: card.expMonth,
                                expYear: card.expYear,
                                cardBrand: card.cardBrand,
                            })}
                        />
                    ))}
                    <Checkable
                        label={t('widgetReservationInfo.card.newCard')}
                        attributes={{
                            type: 'radio',
                            name: 'oldCard',
                            value: 'newCard',
                            onChange: () => {
                                setCardToUse(null);
                            },
                        }}
                    />
                </Grid2>
            )}
            {cardToUse === null && (
                <Grid2 size={{ xs: 12 }}>
                    <Elements
                        options={{ clientSecret: paymentMethod?.methodInfo?.clientSecret }}
                        stripe={stripe}
                    >
                        <StripeInnerForm
                            onStartTransaction={onStartTransaction}
                            paymentMethod={paymentMethod}
                            methodInfo={paymentMethod?.methodInfo}
                            onCancel={onCancel}
                            amount={amount}
                            maxAmount={maxAmount}
                        />
                    </Elements>
                </Grid2>
            )}
            <Grid2 container spacing={2}>
                {onCancel && cardToUse !== null && (
                    <Grid2 size={{ xs: 6 }}>
                        <Button fullWidth onClick={onCancel}>
                            {t('common.cancel')}
                        </Button>
                    </Grid2>
                )}
                {cardToUse && (
                    <Grid2 size={{ xs: 6 }}>
                        <Button
                            fullWidth
                            onClick={payWithOldCard} // cannot do a split if less than 1 usd is one of the parts (either the one paid or the remaining portion)
                            disabled={
                                amount && maxAmount
                                    ? amount < minimumPaymentAmount ||
                                      (maxAmount - amount < minimumPaymentAmount &&
                                          amount !== maxAmount)
                                    : false
                            }
                        >
                            {t('common.pay')}
                        </Button>
                    </Grid2>
                )}
            </Grid2>
        </Grid2>
    );
}

export default StripePaymentBlock;
